import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2
} from '@angular/core';
import {Router} from "@angular/router";
import {Observable, of, Subject, Subscription, timer} from "rxjs";
import {filter, take} from "rxjs/operators";
// import * as tf from '@tensorflow/tfjs';
// import {Tensor, GraphModel} from "@tensorflow/tfjs";

import {BBS_CATEGORY, GUKEO_API_URL1} from "../../defines/protocol";
import {IsContainHttp} from "../../defines/common.func";
import {SimpleWord} from "../../models/simple-word.model";
import {Word} from "../../models/word.model";
import {
  DEFAULT_VOICE_AVG,
  LIST_RECORDER_CONFIG,
  RECORD_STOP_TIMEOUT,
  RECORDER_CONFIG,
  SAMPLE_RATE
} from "../../defines/pitch.const";
import {MP_RESULT} from '../common-popups/msg-popup.component';
import {getWavAmplitude, rmsNormalization, trimNaN} from "../../defines/wav_process.func";
import {getMidPitch} from '../../defines/pitch.func';
import {RECORDER_HELP_BBS_ID} from "../../defines/desktop.const";
import {PitchService} from "../../services/pitch/pitch.service";
import {OptionService} from "../../services/option/option.service";
import {VoiceService} from "../../services/voice/voice.service";
import {TfSpiceService} from "../../services/tf-service/tf-spice.service";
import {RecorderService} from "../../services/recorder/recorder.service";
import {GlobalEventService} from "../../services/global-event/global-event.service";
import {LayoutMgrService} from "../../services/layout-mgr/layout-mgr.service";
import {ClickOutsideMixinFunction} from "../../mixin/click-outside.mixin";
import {ProgressbarMixinFunction} from "../../mixin/progressbar.mixin";
// import {samplePitches} from "../../defines/dummy.const";

/**
 * AudioContext를 OfflineAudioContext로 변경한 이유 (ChatGPT에서 가져옴)
 *
 * AudioContext와 OfflineAudioContext는 모두 Web Audio API에서 제공되는 인터페이스입니다. 그러나 두 인터페이스 간에는 몇 가지 중요한 차이점이 있습니다.
 *
 * 1. 용도
 * AudioContext는 실시간 오디오 처리를 위해 설계되었으며, 실시간으로 오디오 신호를 생성, 처리 및 제어하는 데 사용됩니다.
 * 반면, OfflineAudioContext는 실시간이 아닌 오디오 처리를 위해 설계되었으며, 미리 녹음된 오디오 데이터를 처리하고, 오디오 파일을 생성하는 데 사용됩니다.
 *
 * 2. 시간 개념
 * AudioContext는 실시간 오디오 처리를 수행하기 때문에 시간 개념이 중요합니다. 즉, AudioContext에서 생성된 오디오는 실시간으로 재생되며,
 * 처리 및 제어가 지연 없이 발생해야합니다.
 * 반면, OfflineAudioContext에서는 시간 개념이 덜 중요합니다. 오디오 처리 및 생성은 시간 제약 없이 수행됩니다.
 *
 * 3. 쓰레드
 * AudioContext는 메인 쓰레드에서 실행되며, 실시간 처리를 위해 최적화되어 있습니다.
 * 반면, OfflineAudioContext는 워커 쓰레드에서 실행됩니다.
 * 따라서 메인 쓰레드에서의 처리에 영향을 미치지 않고, 보다 복잡한 처리를 수행할 수 있습니다.
 *
 * 4. 버퍼 사이즈
 * AudioContext에서는 버퍼 크기가 작아야하며, 일반적으로 256 샘플에서 4096 샘플 사이의 크기를 가집니다.
 * 반면, OfflineAudioContext에서는 버퍼 크기가 크거나 작아도 상관없습니다. 따라서 OfflineAudioContext에서는 더 복잡하거나 연산 집약적인 처리를 수행할 수 있습니다.
 *
 * 5. 외부 입력 및 출력
 * AudioContext에서는 실시간 오디오를 처리하기 때문에, 외부 입력 및 출력을 다룰 수 있습니다.
 * 반면, OfflineAudioContext에서는 오디오 파일의 입력 데이터를 처리하고 출력 파일을 생성합니다.
 *
 * 요약하면, AudioContext와 OfflineAudioContext는 모두 Web Audio API에서 제공되는 인터페이스이지만, 실시간 오디오 처리와 미리 녹음된 오디오 처리를 위한 목적에 따라 다른 방식으로 설계되었습니다.
 */
const audioContext = new OfflineAudioContext(1, SAMPLE_RATE, SAMPLE_RATE);

function isEmpty(arr: number[]): boolean {
  return arr.every(v => isNaN(v));
}


const multiMixin = ClickOutsideMixinFunction(
  ProgressbarMixinFunction(
    class Parent {
      constructor(
        public elementRef: ElementRef,
        public renderer: Renderer2,
        public globalEventSvc: GlobalEventService,
      ) {
      }
    }
  )
)

@Component({
  selector: 'app-pitch-popup',
  templateUrl: './pitch-popup.component.html',
  styleUrls: ['./pitch-popup.component.scss'],
})
export class PitchPopupComponent extends multiMixin implements OnInit, OnDestroy {
  @Input() word: SimpleWord | null = null;
  @Output() close = new EventEmitter<SimpleWord | null>();

  protected get ready$(): Observable<boolean> {
    return this.spiceSvc.inited$;
  }

  protected data1: number[] | null = null;
  protected data2: number[] | null = null;

  public recorderConfig: RECORDER_CONFIG | null = null;
  public userConfig: RECORDER_CONFIG = {
    voiceAvg: DEFAULT_VOICE_AVG,
  };

  protected isProcessing = false;

  protected recordStop$ = new Subject<void>();
  private recordStopTimeout$: Subscription | null = null;

  protected hasMic = false; // 마이크 존재 여부


  constructor(
    public override elementRef: ElementRef,
    public override renderer: Renderer2,
    public override globalEventSvc: GlobalEventService,
    protected router: Router,
    protected optionSvc: OptionService,
    protected voiceSvc: VoiceService,
    protected spiceSvc: TfSpiceService,
    protected pitchSvc: PitchService,
    protected recorderSvc: RecorderService,
    protected lm: LayoutMgrService,
  ) {
    super(elementRef, renderer, globalEventSvc);

    this.voiceSvc.Stop();

    this.recorderSvc.hasRecorder().then(hasMic => this.hasMic = hasMic);
  }

  async ngOnInit() : Promise<void> {
    if (this.word?.wordVoice) {

      const urlVoice = this.word?.wordVoice ?? null;

      this.init(urlVoice);
    }
  }

  /**
   * 초기화 함수
   * @param urlVoice - 음성 파일의 url.
   * @param voiceExIdx - 0 이상: 예문 음성 인덱스. 해당 인덱스의 예문을 음성을 재생한다.
   *                     null: 단어 음성을 재생하는 경우
   * @protected
   */
  protected init(urlVoice: string, voiceExIdx: number | null = null): void {

    this.recorderConfig = LIST_RECORDER_CONFIG[this.word!.recorder];

    this.spiceSvc.inited$.pipe(
      filter(inited => inited),
      take(1)
    ).subscribe(() => {
      // console.warn('PitchPopupComponent::init() urlVoice =', urlVoice, voiceExIdx);
      this.drawWordVoice(urlVoice).then(() => {
        // console.warn('PitchPopupComponent::init() this.optionSvc.autoPlay =', this.optionSvc.autoPlay);
        if (this.optionSvc.autoPlay && this.word) {
          this.voiceSvc.PlaySound(this.word, voiceExIdx).subscribe();
        }
      });
    });

    this.coInit(() => this.close.emit(null));

    // 프로그래스바 시작
    this.pbInit();

    // 녹음 중지 처리
    this.recordStop$?.subscribe(() => this.onStopRecord());
  }

  ngOnDestroy(): void {
    this.coDestroy();

    // 프로그래스바 중지
    this.pbExit();

    // 컴포넌트 소멸 시 즉시 isProcessing을 false로 설정
    this.isProcessing = false;

    // 녹음 중지 (recorderSvc.stop()의 반환 타입에 따라 수정 필요)
    if (this.recorderSvc.isRecording) {
      // this.onStopRecord();  // 너무 무겁다.
      this.recorderSvc.stop().subscribe({
        complete: () => {
          console.log('Recording stopped.');
        }
      });
    }

    // 음성 재생 중지
    this.voiceSvc.Stop();

    // 녹음 중지 타임아웃 구독 해제
    this.recordStopTimeout$?.unsubscribe();
    this.recordStopTimeout$ = null;

    // recordStop$ 구독 해제
    this.recordStop$.unsubscribe();
  }

  public onClose(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.globalEventSvc.Click(event);

    this.voiceSvc.Stop();

    this.close.emit(this.word);
  }

  @HostListener('click', ['$event'])
  onClick($event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);
  }

  /*****************************************************
   *  소리 파일 Audio객체에 다운로드
   *****************************************************/
  private async load(url: string): Promise<ArrayBuffer> {
    console.log('PitchPopupComponent::load() url =', url);

    const response: Response = await fetch(url);
    const buffer: ArrayBuffer = await response.arrayBuffer();
    return buffer;
  }


  protected async drawWordVoice(urlVoice: string): Promise<void> {
    console.warn('PitchPopupComponent::drawWordVoice() this.word =', this.word);

    if (!urlVoice) {
      throw new Error(`PitchPopupComponent::onPlay() urlVoice is null ${this.word}`);
    }

    if ((this.data1?.length ?? 0) === 0) {
      const fullUrl = IsContainHttp(urlVoice) ? urlVoice : GUKEO_API_URL1 + urlVoice;

      const buffer = await this.load(fullUrl);
      // console.log('PitchPopupComponent::onPlay() buffer =', buffer);

      this.data1 = await this.processBuffer(buffer);
    }
  }

  protected async processBuffer(arrayBuffer: ArrayBuffer, normalize = false): Promise<Array<number>> {
    // console.log('PitchPopupComponent::processBuffer() arrayBuffer =', arrayBuffer);

    const audioBuffer: AudioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    let channelData: Float32Array = audioBuffer.getChannelData(0);

    if (normalize) {
      // RMS 정규화 적용
      channelData = rmsNormalization(channelData, 0.15);
    }

    const fArray: Array<number> = Array.from(channelData);
    const amplitudes: number[] = getWavAmplitude(fArray);
    // console.log('amplitudes = ', amplitudes, fArray);

    const resultSpice = await this.spiceSvc.predict(channelData);
    // console.log('PitchPopupComponent::onPlay() result =', resultSpice);

    const data = resultSpice.uncertainties.map((uncertainty: number, idx: number) => {
      const amplitude = amplitudes[idx];
      const cond = amplitude > 0.05 && uncertainty < 0.7 && uncertainty - amplitude < 0.3;
      const ret = cond ? resultSpice.pitches[idx] : NaN;
      // console.log('map = ', idx, uncertainty, amplitudes[idx], ret);
      return ret;
    });
    return trimNaN(data);
  }

  /**
   * 녹음 버튼 클릭 처리 함수
   * 녹음을 시작하거나 녹음을 중단함.
   * @param event
   */
  onClickRecord(event: MouseEvent) {
    console.log('PitchPopupComponent::onClickRecord()', this.word);
    event.preventDefault();
    event.stopPropagation();
    this.globalEventSvc.Click(event);

    if (this.isProcessing) {
      return;
    } else if (!this.recorderSvc.isRecording) {
      // 소리 재생 중지.
      this.voiceSvc.Stop();

      this.isProcessing = true;

      this.recorderSvc.start().then(() => {
          // 녹음이 정상적으로 시작되었다면 지정된 시간 후에 녹음을 중지한다.
          this.isProcessing = false;

          this.recordStopTimeout$ = timer(RECORD_STOP_TIMEOUT).subscribe(() => {
            this.recordStop$.next();
          });
        }
      ).catch(err => {
        console.error('PitchPopupComponent::onClickRecord() err =', err);
        let msg = '마이크를 사용 할 수 없습니다.';
        if (err.name === 'PermissionDeniedError' || err.name === 'NotAllowedError') {
          msg = "한국어 홈페이지에서 마이크 사용 권한이 없습니다.<br/>브라우저의 '설정'에서 '마이크 권한'을 추가를 해 주십시오."
        }

        this.coPreventClose();
        this.lm.OpenMsgPopup({
          title: "마이크 오류",
          msg,
          btnOk: "도움말로 이동",
          btnCancel: '닫기',
        }).subscribe(data => {
          console.log(data);
          if (data === MP_RESULT.OK) {
            const userAgent = navigator.userAgent;

            // if (userAgent.indexOf("Chrome") > -1 && userAgent.indexOf("Edg/") === -1) {
            //   // Chrome 브라우저 확인
            // }
            // else if (userAgent.indexOf("Edg/") > -1) {
            //   // Edge 브라우저 확인
            // }

            this.router.navigate(['/board', BBS_CATEGORY.USAGE, RECORDER_HELP_BBS_ID]).then();
          }
          this.coPreventClose(false);
        });

        this.isProcessing = false;

        return;
      });
    } else {
      this.recordStopTimeout$?.unsubscribe();
      this.recordStop$.next();
    }
  }

  /**
   * 녹음 중지 처리 함수
   */
  protected onStopRecord(): void {
    this.isProcessing = true;

    this.recorderSvc.stop().subscribe({
      next: async (arrayBuffer: ArrayBuffer) => {
        console.log('PitchPopupComponent::onClickRecord() arrayBuffer =', arrayBuffer);

        const data: Array<number> = await this.processBuffer(arrayBuffer, true);

        if (!this.word) {
          throw new Error('PitchPopupComponent::onClickRecord() this.word is null');
        }

        const mid = getMidPitch(this.word.iType, data);
        if (!isNaN(mid)) {
          const newMid = this.pitchSvc.setMid(mid);
          // console.warn('voiceAvg = ', this.userConfig.voiceAvg, newMid);
          this.userConfig.voiceAvg = newMid;

          // 정상적으로 중간 값이 처리되었을 때만 그래프를 표시한다.
          this.data2 = data;
        } else {
          // TODO: 녹음 시간이 너무 짧거나, 소리가 작다는 경고 표시하기.
          console.error('PitchPopupComponent::onClickRecord() mid is NaN');

          this.coPreventClose();
          this.lm.OpenMsgPopup({
            title: '음성분석 오류',
            msg: '잘 못 들었습니다.<br />다시 말씀하여 주십시오.',
            btnOk: '확인',
          }).subscribe(data => {
            console.log(data);
            if (data === MP_RESULT.OK) {
              this.coPreventClose(false);
            }
          });
        }
      },
      error: (error) => {
        throw new Error('PitchPopupComponent::onClickRecord() RecordService::stop() is fail:' + error.toString());
      },
      complete: () => {
        this.isProcessing = false;
      }
    });
  }

  OpenWordHelp($event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);

    // this.lm.showHelp = true;
  }

  OpenConfirmReset($event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);
  
    // 확인 팝업이 뜨는 동안 pitch-popup 창이 닫히지 않도록 닫힘 방지 활성화
    this.coPreventClose();
  
    this.lm.OpenMsgPopup({
      title: '사용자를 변경을 하시겠습니까?',
      msg: '고저高低 표시선의 위치가<br>바뀌어진 사용자에게 새로 맞추어집니다.',
      btnOk: '변경',
      btnCancel: '취소',
    }).subscribe(data => {
      console.log(data);
      if (data === MP_RESULT.OK) {
        this.pitchSvc.reset();
        // 아래쪽 그래프 지우기 위해 data2를 초기화합니다.
        this.data2 = null;
      }
      // 팝업 종료 후 닫힘 방지 해제 (원래대로 외부 클릭 시 창이 닫히게 함)
      this.coPreventClose(false);
    });
  }

  protected get btnRecordCss(): string {
    if (!this.hasMic) {
      return 'disabled';
    } else if (this.isProcessing) {
      return 'ing';
    } else {
      return this.recorderSvc.isRecording ? 'on' : ''
    }
  }
}
